import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { AudioRegenChoices } from "@/components/AudioRegenChoices";
import { AudioRegenChoice, useSuggestWording } from "@/hooks/useTranslate";
import {
  likelySentenceIndex,
  mappedSentenceIndex,
  ReaderSelection,
  splitSentences,
  spliceText,
} from "@/lib/selection";

interface WordingSheetProps {
  selection: ReaderSelection | null;
  // Source-language text of the selected segment, for the compact "original"
  // snippet shown alongside the rewording controls.
  sourceText?: string | null;
  // Optional sentence-alignment hint (e.g. "1,3,2,(4,5)"); when present we jump
  // straight to the mapped original sentence instead of the proportional guess.
  sentenceMap?: string | null;
  sourceLanguage: string;
  targetLanguage: string;
  saving: boolean;
  // True when the segment already has narration that this edit will make stale,
  // so the sheet can offer to regenerate it as part of saving.
  hasAudio?: boolean;
  // Receives the segment id, the full new target text (phrase spliced in), and
  // an optional choice for the now-stale narration (omitted = decide later).
  onApply: (
    segmentId: string,
    fullText: string,
    audioPolicy?: AudioRegenChoice,
  ) => void;
  // Shares the selected phrase as a quote (native share sheet / clipboard).
  onShare?: () => void;
  onClose: () => void;
}

/**
 * Bottom sheet for rewording a selected phrase. The reader can either edit the
 * phrase by hand or ask the AI for up to 3 alternatives (each a drop-in
 * replacement). Applying splices the chosen wording back into the full segment
 * and saves it as a human edit.
 */
export function WordingSheet({
  selection,
  sourceText,
  sentenceMap,
  sourceLanguage,
  targetLanguage,
  saving,
  hasAudio,
  onApply,
  onShare,
  onClose,
}: WordingSheetProps) {
  const suggest = useSuggestWording();
  const [draft, setDraft] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const [asked, setAsked] = useState(false);
  // Transient "✓ Copied" confirmation after the Copy button writes to clipboard.
  const [copied, setCopied] = useState(false);
  // Optional choice for the narration this edit makes stale; null = decide later.
  const [audioPolicy, setAudioPolicy] = useState<AudioRegenChoice | null>(null);

  // The original split into sentences, so the reader can step through it: the
  // proportional match is only approximate, so we start near the likely one and
  // let prev/next correct it.
  const sentences = useMemo(
    () => splitSentences(sourceText ?? ""),
    [sourceText],
  );
  const [sentIdx, setSentIdx] = useState(0);
  const activeRef = useRef<HTMLElement | null>(null);

  // On a new selection, jump to the matching sentence. Prefer an explicit
  // sentence map when one is present and resolves; otherwise fall back to the
  // proportional guess (the map is a best-effort hint and may be stale/absent).
  useEffect(() => {
    if (selection) {
      const mapped = mappedSentenceIndex(
        selection.base,
        selection.start,
        selection.end,
        sentences,
        sentenceMap,
      );
      setSentIdx(
        mapped ??
          likelySentenceIndex(
            selection.base,
            selection.start,
            selection.end,
            sentences,
          ),
      );
    }
  }, [selection, sourceText, sentences, sentenceMap]);

  // Keep the focused sentence in view within its (scrollable) box only.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [sentIdx]);

  // Reset whenever a different phrase is opened.
  useEffect(() => {
    setDraft(selection?.text ?? "");
    setOptions([]);
    setAsked(false);
    setCopied(false);
    setAudioPolicy(null);
    suggest.reset();
  }, [selection]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!selection) return null;

  const askAi = async () => {
    const list = await suggest.mutateAsync({
      segmentId: selection.segmentId,
      selectedText: selection.text,
      precedingText: selection.base.slice(0, selection.start),
      followingText: selection.base.slice(selection.end),
    });
    setOptions(list);
    setAsked(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(selection.text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (insecure context / denied) */
    }
  };

  const apply = () => {
    const phrase = draft.trim();
    if (!phrase) return;
    const fullText = spliceText(
      selection.base,
      selection.start,
      selection.end,
      phrase,
    );
    onApply(selection.segmentId, fullText, audioPolicy ?? undefined);
  };

  const unchanged = draft.trim() === selection.text.trim();

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col justify-end"
      role="dialog"
      aria-modal="true"
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30"
      />
      <div className="relative max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white px-5 pb-6 pt-3 shadow-2xl">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-slate-300" />

        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Alternative wording
          </span>
          <div className="-mr-1 flex items-center gap-1">
            <button
              onClick={copy}
              title="Copy quote"
              className="rounded-md px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100"
            >
              {copied ? "✓ Copied" : "⧉ Copy"}
            </button>
            {onShare && (
              <button
                onClick={onShare}
                title="Share quote"
                className="rounded-md px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100"
              >
                ↗ Share
              </button>
            )}
          </div>
        </div>
        <p className="mb-4 font-serif text-sm leading-relaxed text-slate-500">
          “<span className="text-slate-900">{selection.text}</span>”
        </p>

        {sentences.length > 0 && (
          <div className="mb-4 rounded-lg bg-slate-50 px-3 py-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                {sourceLanguage} · likely original
              </span>
              {sentences.length > 1 && (
                <span className="flex items-center gap-1 text-slate-500">
                  <button
                    onClick={() => setSentIdx((i) => Math.max(0, i - 1))}
                    disabled={sentIdx === 0}
                    title="Previous sentence"
                    className="rounded px-1.5 py-0.5 hover:bg-slate-200 disabled:opacity-30"
                  >
                    ‹
                  </button>
                  <span className="text-[10px] tabular-nums text-slate-400">
                    {sentIdx + 1}/{sentences.length}
                  </span>
                  <button
                    onClick={() =>
                      setSentIdx((i) => Math.min(sentences.length - 1, i + 1))
                    }
                    disabled={sentIdx === sentences.length - 1}
                    title="Next sentence"
                    className="rounded px-1.5 py-0.5 hover:bg-slate-200 disabled:opacity-30"
                  >
                    ›
                  </button>
                </span>
              )}
            </div>
            <div className="max-h-20 overflow-y-auto font-serif text-sm leading-snug text-slate-400">
              {sentences.map((s, i) => (
                <Fragment key={i}>
                  {i > 0 ? " " : ""}
                  {i === sentIdx ? (
                    <mark
                      ref={activeRef}
                      className="rounded bg-amber-100/70 text-slate-700"
                    >
                      {s.text}
                    </mark>
                  ) : (
                    s.text
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        )}

        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">
          {targetLanguage} — edit it yourself
        </label>
        <TextareaAutosize
          className="w-full resize-none rounded-lg border border-slate-300 p-2 font-serif text-[1.05rem]"
          minRows={1}
          maxRows={8}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />

        {hasAudio && !unchanged && (
          <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2">
            <div className="mb-1.5 text-[11px] font-medium text-amber-700">
              🔊 This passage has narration — after saving:
            </div>
            <AudioRegenChoices
              size="sm"
              selected={audioPolicy}
              onChoose={(c) =>
                setAudioPolicy((prev) => (prev === c ? null : c))
              }
            />
            <div className="mt-1.5 text-[10px] text-amber-600/80">
              Leave unset to decide later from the reader.
            </div>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={apply}
            disabled={saving || unchanged || !draft.trim()}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? "Applying…" : "Apply"}
          </button>
          <button
            onClick={askAi}
            disabled={suggest.isPending}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            {suggest.isPending ? "Asking AI…" : "✨ Ask AI"}
          </button>
          <button
            onClick={onClose}
            className="ml-auto px-2 py-1.5 text-sm text-slate-500"
          >
            Cancel
          </button>
        </div>

        {suggest.isError && (
          <p className="mt-3 text-sm text-red-600">
            Couldn’t get suggestions. Try again.
          </p>
        )}

        {asked && !suggest.isPending && options.length === 0 && (
          <p className="mt-3 text-sm text-slate-500">
            This wording already reads well — no changes suggested.
          </p>
        )}

        {options.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              Tap a suggestion to use it
            </div>
            {options.map((opt, i) => (
              <button
                key={i}
                onClick={() => setDraft(opt)}
                className={`block w-full rounded-lg border p-3 text-left font-serif text-[1.05rem] leading-relaxed hover:bg-slate-50 ${
                  draft === opt
                    ? "border-slate-900 bg-slate-50"
                    : "border-slate-200"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
